/**
 * 튜닝 사례 → 문서(마크다운) — 사례 상세를 SQL 튜닝 리포트와 같은 모양(ReportView)으로 보이기 위해(사용자 요청 2026-10-11).
 *
 * 저장값은 바꾸지 않는다. 보여 줄 때만:
 *  - 「개선 효과」 글에서 「이름 전 → 후」 짝을 뽑아 감소율 막대 표로(utils/reductionBar — 리포트와 같은 막대)
 *  - 본문의 제목(##)은 한 단계 내려 「내용」 아래에 둔다
 *  - 실행계획은 칸을 화면 폭 기준으로 다시 맞춘다(utils/planAlign — 판독·복사로 어긋난 표와 한글 이름 줄의 세로선)
 *  - 본문·유의사항이 언급한 계획 줄(「Id 12」 등)에 `◀ [n]` 을 달고 계획 아래에 그 설명을 붙인다(utils/planRefs — 리포트의 「◀ D1」)
 * 문구는 전부 t() 로 받는다(이 파일에 화면 문구를 적지 않는다).
 */
import { figureSrc } from '../../utils/caseMeta';
import { parseImprovement, reductionPct, barText, amountText, unitOf, secNumber } from '../../utils/reductionBar';
import { alignPipeTables } from '../../utils/planAlign';
import { formatPlanText } from '../../utils/planTextFormat';
import { planNotes, markPlan } from '../../utils/planRefs';

const fence = (text, lang = '') => `\`\`\`${lang}\n${String(text || '').replace(/```/g, '``​`')}\n\`\`\``;
const esc = (v) => String(v == null ? '' : v).replace(/\|/g, '\\|').replace(/\r?\n/g, ' ');

/** 실행계획 — 한 줄 요약이면 표로 되살리고(formatPlanText), 표는 칸을 맞춘다 */
export function planForDoc(text) {
  const f = formatPlanText(text);
  return { text: alignPipeTables(f.reformatted ? f.text : text), inferred: !!(f.reformatted && f.inferredDepth) };
}

/**
 * @param c        사례(서버 _case 모양)
 * @param t        i18next t
 * @param catalog  분류 유형 목록 [{ code, axis, label }] — 축별로 묶어 라벨로 보인다
 * @param opts.brief 축약 — 개선 효과·분류·내용·유의사항만(SQL·실행계획은 뺀다). SQL 튜닝 리포트의 「축약」 과 같은 뜻
 */
/**
 * 설명이 필요한 줄 — 본문(절 제목이 문맥)·유의사항이 언급한 Id 를 그 계획에 표시한다. 문서와 편집 화면의 경고가 **같은 함수**를 쓴다
 * (둘이 따로 판정하면 경고는 없는데 표시가 빠지는 일이 생긴다).
 * @returns { aligned: { beforePlan, afterPlan }, refs: planNotes 결과 } — 계획이 하나도 없으면 refs 는 null
 */
export function caseRefs(c) {
  const aligned = { beforePlan: c.beforePlan ? planForDoc(c.beforePlan) : null, afterPlan: c.afterPlan ? planForDoc(c.afterPlan) : null };
  if (!aligned.beforePlan && !aligned.afterPlan) return { aligned, refs: null };
  const refs = planNotes(
    [{ text: c.body, defaultSide: 'before' }, { text: c.caution, defaultSide: 'before' }],
    { before: aligned.beforePlan ? aligned.beforePlan.text : '', after: aligned.afterPlan ? aligned.afterPlan.text : '' },
  );
  return { aligned, refs };
}

/**
 * 본문의 소제목(### — 「내용」 아래로 한 단계 내린 것)에 절 번호를 붙인다 — 「3.1 원인」·「3.2 근거」(2026-10-24 사용자 요청: 가독성).
 * 이미 번호로 시작하는 제목(「1. 원인」·「2) 근거」)은 그대로 두고, 코드 블록 안의 줄은 건드리지 않는다. 더 깊은 제목(####)은 두지 않는다.
 */
export function numberSubheadings(md, sec) {
  const lines = String(md || '').split('\n');
  // 본문에서 가장 얕은 제목 단계를 소제목(n.m)으로, 그 아래 단계를 n.m.k 로 — 사례마다 ## 로 쓰기도 ### 로 쓰기도 한다
  let inFence = false;
  let top = 0;
  for (const line of lines) {
    if (/^\s*```/.test(line)) { inFence = !inFence; continue; }
    const m = !inFence && /^(#{3,6}) \S/.exec(line);
    if (m && (!top || m[1].length < top)) top = m[1].length;
  }
  if (!top) return lines.join('\n');
  inFence = false;
  let sub = 0;
  let subsub = 0;
  return lines.map((line) => {
    if (/^\s*```/.test(line)) { inFence = !inFence; return line; }
    if (inFence) return line;
    const m = /^(#{3,6}) (.+)$/.exec(line);
    if (!m || /^\d+([.)]|\.\d+)/.test(m[2].trim())) return line;
    if (m[1].length === top) { sub += 1; subsub = 0; return `${m[1]} ${sec}.${sub} ${m[2]}`; }
    if (m[1].length === top + 1 && sub > 0) { subsub += 1; return `${m[1]} ${sec}.${sub}.${subsub} ${m[2]}`; }
    return line;
  }).join('\n');
}

export function caseDocument(c, t, catalog = [], { brief = false, meta = true } = {}) {
  if (!c) return '';
  const out = [];
  // 절 번호 — 있는 절만 차례로 센다(개선 효과가 없으면 분류가 1.). 소제목은 n.m
  let sec = 0;
  let sub = 0;
  const h2 = (title) => { sec += 1; sub = 0; out.push(`## ${sec}. ${title}`); };
  const h3 = (title) => { sub += 1; out.push(`### ${sec}.${sub} ${title}`); };
  const label = (code) => (catalog.find(x => x.code === code) || {}).label || code;
  const axisOf = (code) => (catalog.find(x => x.code === code) || {}).axis || null;

  // 머리 — 무엇에 관한 사례인지(DBMS·공개·폴더)
  const showMeta = meta;
  const metaParts = [
    c.dbms ? `${c.dbms}${c.dbmsVersion ? ` ${c.dbmsVersion}` : ''}` : null,
    t(`assistant:caseList.vis.${c.visibility}`),
    t(`assistant:caseList.status.${c.status}`),
    c.folder ? `${t('assistant:caseFolder.field')}: ${c.folder}` : null,
  ].filter(Boolean);
  if (showMeta) out.push(`- ${metaParts.join(' · ')}`);
  if (c.symptom) out.push(`- ${t('assistant:caseDoc.symptomText')}: ${c.symptom}`);
  // 원본 파일 — 첨부로 만든 사례가 무엇에서 왔는지(주인에게만 온다)
  if ((c.sourceFiles || []).length) out.push(`- ${t('assistant:caseDoc.sourceFiles')}: ${c.sourceFiles.map(f => `\`${String(f).replace(/`/g, "'")}\``).join(', ')}`);
  if (brief) out.push(`- *${t('assistant:caseDoc.briefNote')}*`);
  out.push('');

  // 1. 개선 효과 — 짝이 뽑히면 막대 표, 원문 글은 그 아래에 그대로
  if (c.improvement) {
    h2(t('assistant:caseDoc.effect'));
    out.push('');
    const pairs = parseImprovement(c.improvement);
    if (pairs.length) {
      out.push(`| ${t('assistant:caseDoc.metric')} | ${t('assistant:caseDoc.before')} | ${t('assistant:caseDoc.after')} | ${t('assistant:caseDoc.amount')} | ${t('assistant:caseDoc.rate')} |`);
      out.push('|---|---:|---:|---:|---|');
      for (const p of pairs) {
        const bar = barText(reductionPct(p.before, p.after));
        // 감소량(전 − 후) — 원문이 「10M」 처럼 줄여 적은 값이면 어림이다
        const amount = amountText(p.before, p.after, { isTime: p.isTime, approx: /[KMG]/.test(`${p.beforeText}${p.afterText}`) });
        // 단위는 지표 이름 옆에 작게(「*(…)*」 — ReportView 가 작은 글씨로 그린다) 적고 값 칸에서는 뺀다(사용자 요청 2026-10-12).
        // 시간은 초로 맞춘 숫자, 그 밖은 원문 글자 그대로(「323K」 같은 줄임 표기 포함)
        const unit = unitOf(p.label, p.isTime);
        const name = `${esc(p.label || (p.isTime ? t('assistant:caseDoc.elapsed') : '-'))}${unit ? ` *(${t(`assistant:caseDoc.unit.${unit}`)})*` : ''}`;
        const val = (text, v) => (p.isTime ? secNumber(v) : esc(text));   // 시간이 아닌 값에는 단위 글자가 없다(parseImprovement 가 시간 단위만 붙인다)
        out.push(`| ${name} | ${val(p.beforeText, p.before)} | ${val(p.afterText, p.after)} | ${amount || '-'} | ${bar || '-'} |`);
      }
      out.push('');
    }
    out.push(`> ${esc(c.improvement)}`);
    out.push('');
  }

  // 2. 분류 — 증상·원인·처방 축으로
  const topics = c.topics || [];
  if (topics.length) {
    h2(t('assistant:caseDoc.topics'));
    out.push('');
    for (const axis of ['symptom', 'cause', 'fix', 'area']) {
      const xs = topics.filter(code => axisOf(code) === axis);
      if (xs.length) out.push(`- **${t(`assistant:topicAxis.${axis}`)}**: ${xs.map(label).join(' · ')}`);
    }
    const rest = topics.filter(code => !['symptom', 'cause', 'fix', 'area'].includes(axisOf(code)));
    if (rest.length) out.push(`- ${rest.map(label).join(' · ')}`);
    out.push('');
  }
  // 2-1. 용도·근거(선택 칸) — 이 사례를 얼마나 믿고 어디에 쓰는지. 정한 것만 적는다
  const metaLine = [];
  // 화면 머리줄이 칩으로 보이면(meta:false) 용도·근거는 다시 적지 않는다
  if (showMeta && Array.isArray(c.usage) && c.usage.length) metaLine.push(`**${t('assistant:caseMeta.usage')}**: ${c.usage.map(u => t(`assistant:caseMeta.u.${u}`)).join(' · ')}${c.usageDerived ? ` (${t('assistant:caseMeta.auto')})` : ''}`);
  if (showMeta && c.perfBasis) metaLine.push(`**${t('assistant:caseMeta.perfBasis')}**: ${t(`assistant:caseMeta.basis.${c.perfBasis}`)}`);
  if (c.resultSame) metaLine.push(`**${t('assistant:caseMeta.resultSame')}**: ${t(`assistant:caseMeta.same.${c.resultSame}`)}`);
  if (metaLine.length && !brief) { out.push(metaLine.join(' / ')); out.push(''); }

  // 3. 내용 — 본문의 제목은 한 단계 내린다
  if (c.body) {
    h2(t('assistant:caseDoc.body'));
    out.push('');
    out.push(numberSubheadings(String(c.body).replace(/^(#{1,5}) /gm, '#$1 '), sec));
    out.push('');
  }
  // 3-1. 그림 — 원문의 설명용 이미지(데이터 분포·액세스 범위 도식 등). 본문 바로 아래에, 설명과 함께(축약에도 싣는다 — 글보다 빨리 읽힌다)
  const figures = Array.isArray(c.figures) ? c.figures.filter(f => f && f.id) : [];
  if (figures.length) {
    h2(t('assistant:caseDoc.figures'));
    out.push('');
    figures.forEach((f, i) => {
      const cap = String(f.caption || '').replace(/\s+/g, ' ').trim();
      out.push(`![${(cap || `${t('assistant:caseDoc.figure')} ${i + 1}`).replace(/[[\]]/g, '')}](${figureSrc(f)})`);
      out.push('');
      out.push(`*${t('assistant:caseDoc.figure')} ${i + 1}${cap ? ` — ${cap.replace(/\*/g, '')}` : ''}*`);
      out.push('');
    });
  }
  if (c.kind === 'table') {
    if (c.fix) { h2(t('assistant:caseDoc.fix')); out.push('', c.fix, ''); }
    if (c.evidence) { h2(t('assistant:caseDoc.evidence')); out.push('', c.evidence, ''); }
  }

  // 4. 유의사항 — 따르면 안 되는 조건이 여기 있다
  if (c.caution) {
    h2(t('assistant:caseDoc.caution'));
    out.push('');
    out.push(String(c.caution).split('\n').map(l => `> ${l}`).join('\n'));
    out.push('');
  }

  if (brief) return out.join('\n');

  // 5. SQL
  if (c.beforeSql || c.afterSql || c.indexDdl) {
    h2(t('assistant:caseDoc.sql'));
    out.push('');
    if (c.beforeSql) { h3(t('assistant:caseList.beforeSql')); out.push(fence(c.beforeSql, 'sql'), ''); }
    if (c.afterSql) { h3(t('assistant:caseList.afterSql')); out.push(fence(c.afterSql, 'sql'), ''); }
    if (c.indexDdl) { h3(t('assistant:caseList.indexDdl')); out.push(fence(c.indexDdl, 'sql'), ''); }
  }

  // 6. 실행계획 — 칸을 맞춰서
  if (c.beforePlan || c.afterPlan) {
    h2(t('assistant:caseDoc.plan'));
    out.push('');
    const { aligned, refs } = caseRefs(c);
    for (const key of ['beforePlan', 'afterPlan']) {
      const p = aligned[key];
      if (!p) continue;
      const side = key === 'beforePlan' ? 'before' : 'after';
      h3(t(`assistant:caseList.${key}`));
      if (p.inferred) out.push(`*${t('assistant:caseList.planInferred')}*`);
      out.push(fence(markPlan(p.text, refs.marks[side])), '');
      const mine = refs.notes.filter(n => n.side === side);
      if (mine.length) {
        out.push(`${t('assistant:caseDoc.markedLines')}`);
        out.push('');
        for (const n of mine) out.push(`- **[${n.no}]** ${n.text}`);
        out.push('');
      }
    }
  }
  return out.join('\n');
}
